import { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import LanguageDropdown from "components/__reusable/LanguageDropdown";
import Breadcrumbs from "./Breadcrumbs";
import {
  BASE_PATH,
  FACILITY_PATH,
  PICTURES_PATH,
  SYSTEM_INFO_PATH,
  SETTING_PATH,
  ACCOUNT_PATH,
  CLASS_PATH,
  UPLOAD_PATH,
  UNPUBLISHED_PICTURES_PATH,
  PICTURES_SUMMARIZATION_PATH,
  DELETE_PICTURES_PATH,
  PARENTS_MATERIAL_PATH,
  USER_MANUAL_PATH,
  FAQ_PATH,
} from "router/pathName";
import { OPEN_NAV } from "config/keywords";
import useAppDispatch from "hooks/useAppDispatch";
import useAppSelector from "hooks/useAppSelector";
import { getClasses } from "features/class/classAction";
import FacilityLabel from "./FacilityLabel";
import HelpButton from "./HelpButton";
import HamburgerMenuIcon from "assets/icons/HamburgerMenuIcon";

const FACILITY_PATH_REGEX = new RegExp(FACILITY_PATH);
const PICTURES_PATH_REGEX = new RegExp(PICTURES_PATH);
const SYSTEM_INFO_PATH_REGEX = new RegExp(SYSTEM_INFO_PATH);
const SETTING_PATH_REGEX = new RegExp(SETTING_PATH);
const ACCOUNT_PATH_REGEX = new RegExp(ACCOUNT_PATH);
const CLASS_PATH_REGEX = new RegExp(CLASS_PATH);
const UPLOAD_PATH_REGEX = new RegExp(UPLOAD_PATH);
const UNPUBLISHED_PICTURES_PATH_REGEX = new RegExp(UNPUBLISHED_PICTURES_PATH);
const PICTURES_SUMMARIZATION_PATH_REGEX = new RegExp(
  PICTURES_SUMMARIZATION_PATH,
);
const DELETE_PICTURES_PATH_REGEX = new RegExp(DELETE_PICTURES_PATH);
const USER_MANUAL_PATH_REGEX = new RegExp(USER_MANUAL_PATH);
const FAQ_PATH_REGEX = new RegExp(FAQ_PATH);

const Header = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { classes } = useAppSelector((state) => state.class);

  const title = useMemo(() => {
    const pathName = location.pathname;

    switch (true) {
      case pathName === BASE_PATH:
        return "_header._home";
      case PICTURES_SUMMARIZATION_PATH_REGEX.test(pathName):
        return "_header._home";
      case PICTURES_PATH_REGEX.test(pathName):
        return "_header._picture_management";
      case CLASS_PATH_REGEX.test(pathName):
        return "_header._class_management";
      case ACCOUNT_PATH_REGEX.test(pathName):
        return "_header._account_management";
      case SYSTEM_INFO_PATH_REGEX.test(pathName):
        return "_header._notification";
      case FACILITY_PATH_REGEX.test(pathName):
        return "_header._facility_information";
      case SETTING_PATH_REGEX.test(pathName):
        return "_header._setting";
      case UPLOAD_PATH_REGEX.test(pathName):
        return "_header._upload";
      case DELETE_PICTURES_PATH_REGEX.test(pathName):
        return "_header._delete_picture_management";
      case UNPUBLISHED_PICTURES_PATH_REGEX.test(pathName):
        return "_header._unpublished_picture_management";
      case pathName === PARENTS_MATERIAL_PATH:
        return "_header._parents_material";
      case USER_MANUAL_PATH_REGEX.test(pathName):
        return "_header._user_manual";
      case FAQ_PATH_REGEX.test(pathName):
        return "_header._faq";
      default:
        return "";
    }
  }, [location.pathname]);

  const contextClassId = useMemo(() => {
    const matches = [
      location.pathname.match(/^\/class\/(\d+)(?:\/student(?:\/\d+)?)?$/),
      location.pathname.match(/^\/publish\/(\d+)$/),
      location.pathname.match(/^\/delete\/(\d+)$/),
    ];

    return matches.find(Boolean)?.[1] || null;
  }, [location.pathname]);

  useEffect(() => {
    if (contextClassId && classes.length === 0) {
      dispatch(getClasses());
    }
  }, [classes.length, contextClassId, dispatch]);

  const subtitle = useMemo(() => {
    if (!contextClassId) return "";

    const currentClass = classes.find(
      (classItem) => classItem.id === Number(contextClassId),
    );

    return currentClass ? `${currentClass.year} - ${currentClass.name}` : "";
  }, [classes, contextClassId]);

  return (
    <header className="flex items-center justify-between border-b border-l border-[#E0E0E0] bg-white px-3 py-2 lg:px-4">
      <div className="min-w-0 flex-1">
        <Breadcrumbs fallbackTitle={t(title)} fallbackSubtitle={subtitle} />
      </div>
      <div className="hidden w-fit items-center space-x-4 lg:flex">
        <div className="w-fit">
          <LanguageDropdown variant="header" />
        </div>
        <HelpButton />
        <FacilityLabel />
      </div>
      <div className="flex items-center space-x-3 lg:hidden">
        <HelpButton />
        <button onClick={() => window.dispatchEvent(new Event(OPEN_NAV))}>
          <HamburgerMenuIcon width={40} height={40} color={"#767676"} />
        </button>
      </div>
    </header>
  );
};

export default Header;
